import {
  AuthUser,
  getUser,
  getUserPermission,
  LoginCredentials,
  loginWithEmailAndPassword,
  logout,
  register,
  RegisterCredentials,
} from '@/features/auth';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';
import { useMutation, useQuery } from '@tanstack/react-query';
import { configureAuth } from 'react-query-auth';
import { LICENSE_TYPE, type LicenseType } from '@/config';

async function userFn() {
  return await getUser();
}

async function userPermissionFn() {
  return await getUserPermission();
}

async function registerFn(data: RegisterCredentials) {
  return await register(data);
}

async function loginFn(data: LoginCredentials) {
  return await loginWithEmailAndPassword(data);
}

async function logoutFn() {
  await logout(getCurrentLanguage());
}

export const { useUser, useLogin, useLogout, AuthLoader } = configureAuth<
  AuthUser | null,
  unknown,
  LoginCredentials,
  RegisterCredentials
>({
  userFn,
  loginFn,
  logoutFn,
  registerFn,
});

// register すると user が reactQuery によってキャッシュされ、瞬ログイン状態みたいになってバグるので、
// react query auth の useResister は使わない
export const useRegister = () => {
  return useMutation({
    mutationFn: registerFn,
  });
};

export const useUserPermission = () => {
  const query = useQuery({
    queryKey: ['useUserPermission'],
    queryFn: userPermissionFn,
    staleTime: 1000 * 60 * 3, // 3 minutes
  });

  const summarizedRole = query.data?.summarizedRole;
  const grantedLicenseTypes = query.data?.grantedLicenseTypes;
  const isOnlyBasicLicense = onlyHasLicense(
    grantedLicenseTypes,
    LICENSE_TYPE.BASIC
  );
  const hasEduLicense = Boolean(
    grantedLicenseTypes?.includes(LICENSE_TYPE.EDU)
  );
  const isSystemAdmin = summarizedRole === 'system_admin';

  return {
    ...query,
    isOnlyBasicLicense,
    hasEduLicense,
    isSystemAdmin,
  };
};

const onlyHasLicense = (
  grantedLicenseTypes: readonly LicenseType[] | undefined,
  licenseType: LicenseType
) => {
  return (
    grantedLicenseTypes?.length === 1 && grantedLicenseTypes[0] === licenseType
  );
};
